/// The loops Multiply offers, and the maths behind their headline figures.
///
/// The offered set lives in config/loops.json - the catalogue pattern config/reserves.json and
/// config/launchVaults.json already follow, extended to Multiply at the founder's call: expanding
/// the product line (a looped-stablecoin pair, a second LST loop) is a catalogue entry, never a
/// code edit. Per-loop design rationale, the offer rules, and the withdrawn-and-relisted lesson
/// all live in that file's $comment blocks, next to the entries they explain.
///
/// Every figure a loop advertises is derived from the SAME on-chain reserve data the Markets page
/// renders - collateral factors, supply and borrow rates, live prices, emission budgets - rather
/// than from numbers in the catalogue. A loop's economics are entirely a function of the two
/// reserves it sits between, so anything written into config would be a second source of truth
/// that could drift from the one the pool actually enforces; the catalogue carries identity and
/// story only.
import loopsCatalogue from '../../config/loops.json'

export const LOOPS = loopsCatalogue.loops

/// Exposure converges to `1 / (1 - collateralFactor)` - each pass supplies, borrows what the fresh
/// collateral allows, and buys more. Capped here at what the reserve actually permits, so this
/// number can never promise leverage the pool would refuse.
export function maxLeverage(collateralFactorBps) {
  if (!collateralFactorBps || collateralFactorBps >= 10_000) return 0
  return 10_000 / (10_000 - collateralFactorBps)
}

/// Net APY at a given leverage. At L times exposure the position earns the given earn rate on all
/// L, and pays the given cost rate on the (L - 1) that was borrowed.
///
/// Pure math over whatever rates the caller passes. Callers now pass ALL-IN legs (interest plus
/// HORIZON at spot plus intrinsic collateral yield, via loopLegRates below) - a founder decision
/// recorded in CHANGELOG.md item 195 that inverted the earlier keep-HORIZON-out rule; the price
/// assumption is named in help text at every surface instead of shaping the number silently.
export function netApyBps(supplyRateBps, borrowRateBps, leverage) {
  if (!leverage) return 0
  return supplyRateBps * leverage - borrowRateBps * (leverage - 1)
}

/// Net APY sampled across the whole leverage range, for the detail page's curve.
///
/// The reference charts historical net APY at four fixed multipliers. Sampling the live curve
/// instead shows the one thing a looper most needs and a history of four fixed points cannot: WHERE
/// THE LINE CROSSES ZERO. Past that leverage the borrow cost exceeds what the extra exposure earns,
/// and the position loses money by design rather than by misfortune. That crossing moves with the
/// rates, so it has to be computed live.
export function buildLeverageCurve(supplyRateBps, borrowRateBps, maxLev, samples = 60) {
  if (!maxLev || maxLev <= 1) return []
  const points = []
  for (let i = 0; i < samples; i++) {
    const lev = 1 + ((maxLev - 1) * i) / (samples - 1)
    points.push({ leverage: lev, netApyPct: netApyBps(supplyRateBps, borrowRateBps, lev) / 100 })
  }
  return points
}

/// The leverage at which net APY reaches zero, or null when the curve stays positive throughout.
///
/// Solving `supply x L - borrow x (L - 1) = 0` gives `L = borrow / (borrow - supply)`, which is
/// meaningful only while the borrow rate exceeds the supply rate - when it does not, leverage
/// improves the return at every level and there is no crossing to mark.
export function breakEvenLeverage(supplyRateBps, borrowRateBps, maxLev) {
  if (borrowRateBps <= supplyRateBps) return null
  const lev = borrowRateBps / (borrowRateBps - supplyRateBps)
  if (!Number.isFinite(lev) || lev <= 1 || lev > maxLev) return null
  return lev
}

/// Reward APR as a deposit would actually receive it: emissions divided by the pool as it stands
/// AFTER that deposit joins. This is EarnSection's cold-start estimate generalised (same $-basis
/// reasoning, same no-fabricated-data rule: every input real, the deposit assumption stated by the
/// caller's label) - moved here so Multiply and Earn share one implementation. The plain APR math
/// in useProtocolState deliberately reports 0% on an empty market; that truth made exactly the
/// loops that most need a first opener (wstETH, WBTC: zero supplied, live emission streams) look
/// like they pay nothing.
export function dilutedRewardAprBps(emissionsUsdPerYear, poolUsd, addUsd) {
  if (!(emissionsUsdPerYear > 0)) return 0
  const denom = (poolUsd > 0 ? poolUsd : 0) + addUsd
  if (!(denom > 0)) return 0
  return (emissionsUsdPerYear / denom) * 10_000
}

/// Both legs of a loop as effective bps rates, deposit-aware on the reward streams.
/// `intrinsicAprBps` is yield the collateral accrues by its own exchange rate (wstETH staking) -
/// invisible to both interest and emissions, real to the position.
///
/// Emissions enter as the UNGOVERNED budget bounded by the APR governor's cap - `min(diluted
/// budget, capBps)` - which is exactly what accrual pays a deposit once it exists: the governor's
/// stream is `min(budget, cap x farmTvl)`, and dividing by the post-deposit TVL reduces to that
/// min(). Feeding TODAY'S effective rate here instead repeats the entry-198 bug in leveraged form:
/// an empty farm's effective rate is zero, so the loops that most need a first opener (USDC/USDx
/// on launch day) advertised only their negative interest carry while a real capped stream waited.
export function loopLegRates({
  supplyRateBps, borrowRateBps, intrinsicAprBps = 0,
  supplyEmissionsUsdPerYear = 0, borrowEmissionsUsdPerYear = 0,
  supplyCapBps = 0, borrowCapBps = 0,
  suppliedUsd = 0, borrowedUsd = 0, supplyAddUsd = 0, borrowAddUsd = 0,
}) {
  const capped = (raw, capBps) => (capBps > 0 ? Math.min(raw, capBps) : raw)
  return {
    earnBps: supplyRateBps + intrinsicAprBps
      + capped(dilutedRewardAprBps(supplyEmissionsUsdPerYear, suppliedUsd, supplyAddUsd), supplyCapBps),
    costBps: borrowRateBps
      - capped(dilutedRewardAprBps(borrowEmissionsUsdPerYear, borrowedUsd, borrowAddUsd), borrowCapBps),
  }
}

/// Which story today's rates tell about return against leverage. `breakEvenLeverage` returning
/// null covers three situations with OPPOSITE meanings - the curve stays positive throughout, the
/// curve is negative from the first turn of leverage, or the crossing sits past the ceiling - and
/// chart copy that treats null as "always earns" captions a losing loop as a winning one (found
/// live: ETH supply 0.00% under USDx borrow 0.02%, falling curve, caption claiming every level
/// improves the return). One resolver, so every surface tells the same story.
///
/// Kinds: `crossesWithinRange` (earns up to `breakEven`, costs beyond), `earnsEverywhere`,
/// `flat` (the two rates sit level), `crossesBeyondCeiling` (earns at every reachable level,
/// declining), `costsEverywhere` (each level of leverage deepens the cost).
export function loopReturnCase(supplyRateBps, borrowRateBps, maxLev) {
  const breakEven = breakEvenLeverage(supplyRateBps, borrowRateBps, maxLev)
  if (breakEven != null) return { kind: 'crossesWithinRange', breakEven }
  if (borrowRateBps === supplyRateBps) return { kind: 'flat', breakEven: null }
  if (borrowRateBps < supplyRateBps) return { kind: 'earnsEverywhere', breakEven: null }
  const lev = borrowRateBps / (borrowRateBps - supplyRateBps)
  if (Number.isFinite(lev) && lev > maxLev) return { kind: 'crossesBeyondCeiling', breakEven: null }
  return { kind: 'costsEverywhere', breakEven: null }
}

/// How to READ a loop net-APY figure, which differs by loop type and must not be guessed from its
/// sign. On a yield loop a negative number is a warning - the borrow costs more than the collateral
/// earns, so leverage is destroying value and the user should see that in alarm colours. On a
/// directional loop the same negative number is the ordinary price of holding exposure, and
/// painting it red would misrepresent a correct product as a broken one.
///
/// Returns the LABEL and the TONE, so every surface renders the distinction the same way rather
/// than each one re-deciding it.
export function carryPresentation(loop, netApyBps) {
  if (loop?.directional) {
    return netApyBps >= 0
      ? { label: 'Net carry', tone: 'positive', note: 'earned while held' }
      : { label: 'Cost to hold', tone: 'neutral', note: 'the price of leverage, charged while open' }
  }
  return netApyBps >= 0
    ? { label: 'Net APY', tone: 'positive', note: 'earned while held' }
    : { label: 'Net APY', tone: 'negative', note: 'the borrow costs more than the collateral earns' }
}
/// Current leverage of an open position: total exposure over the user's own equity in it.
export function currentLeverage(exposureUsd, debtUsd) {
  const equity = exposureUsd - debtUsd
  return equity > 0 ? exposureUsd / equity : 0
}

/// The price at which a looped position becomes liquidatable, expressed in the DEBT asset.
///
/// Liquidation is when `collateral x price x threshold == debt`. Solving for price gives the level
/// the collateral has to reach, which is a far more actionable statement than a health factor -
/// it is a number someone can compare against a chart.
export function liquidationPrice(collateralAmount, debtValueInDebtAsset, liquidationThresholdBps) {
  if (collateralAmount <= 0 || !liquidationThresholdBps) return null
  return debtValueInDebtAsset / (collateralAmount * (liquidationThresholdBps / 10_000))
}

/// Current equity and P&L for an open loop, denominated in the COLLATERAL asset.
///
/// Collateral rather than dollars, deliberately. A dollar P&L needs the collateral's price at every
/// deposit, which this app cannot reconstruct without an indexer — the same limitation
/// `useHistoricalStats` records for its own series. Denominating in the collateral needs only today's
/// ratio, so it is a real number rather than an approximated one: "you put in 5 ETH and your stake is
/// now worth 5.4 ETH" is both honest and the figure a levered holder actually cares about.
///
/// `position` comes from `useLoopPosition`, which reconstructs the cost basis from the router's own
/// events (CHANGELOG.md item 123). Returns null rather than zeroes when the basis is unknown or the
/// price ratio is unusable — a P&L of "0" reads as "flat", which is a claim, whereas null lets the
/// caller render nothing.
export function computeLoopPnl({ position, loop }) {
  if (!position?.known) return null
  const { collateral, debt } = loop
  const spotRatio = debt.priceUsd > 0 ? collateral.priceUsd / debt.priceUsd : 0
  if (!(spotRatio > 0)) return null

  // Debt converted into collateral units at today's ratio, so equity is one denomination throughout.
  const debtInCollateral = debt.user.debt / spotRatio
  const equity = collateral.user.supplyBalance - debtInCollateral
  const pnl = equity - position.principal
  return {
    equity,
    pnl,
    pnlPct: position.principal > 0 ? (pnl / position.principal) * 100 : null,
    spotRatio,
    // How far the pair has moved since entry, which is what actually drives the number above.
    priceChangePct: position.entryPrice > 0
      ? ((spotRatio - position.entryPrice) / position.entryPrice) * 100
      : null,
  }
}
